import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHero from '../../../components/shared/PageHero';
import Sidebar from '../../../components/shared/Sidebar';
import { getProgramBySlug, programs } from '../data/programs';
import {
  Clock, CreditCard, GraduationCap, Briefcase, CheckCircle2, BookOpen,
  ChevronRight, Users, Award, ArrowRight
} from 'lucide-react';

export default function ProgramDetail() {
  const { i18n, t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const program = slug ? getProgramBySlug(slug) : undefined;

  if (!program) return <Navigate to="/programs" replace />;

  const currentLang = (i18n.language.split('-')[0] as 'en' | 'fr') || 'fr';
  const data = program.translations[currentLang] || program.translations.fr;

  // Index of program for prev/next navigation
  const currentIdx = programs.findIndex(p => p.slug === slug);
  const prevProgram = currentIdx > 0 ? programs[currentIdx - 1] : null;
  const nextProgram = currentIdx < programs.length - 1 ? programs[currentIdx + 1] : null;

  return (
    <div className="flex flex-col bg-white">
      <PageHero
        title={data.title}
        description={data.subtitle}
        breadcrumbs={[
          { label: t('nav_programs'), href: '/programs' },
          { label: data.title, href: `/programs/${program.slug}` }
        ]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-12 md:py-20 px-4 sm:px-6 lg:px-12 flex flex-col lg:flex-row gap-12 lg:gap-16">
        <Sidebar variant="programs" />

        <div className="flex-1 space-y-20 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">

          {/* Hero Image */}
          <div className="w-full h-64 md:h-96 overflow-hidden rounded-sm shadow-2xl border-b-8 border-gold relative group">
            <img
              src={program.heroImage}
              alt={data.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <span className="inline-block text-[10px] font-black uppercase tracking-[0.3em] text-gold border border-gold/40 px-3 py-1 rounded-full bg-navy/40 backdrop-blur-sm">
                {data.category}
              </span>
            </div>
          </div>

          {/* Quick Info Bar */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-6 border-b-2 border-gray-100 pb-10">
            {[
              { icon: <Clock className="w-5 h-5 text-gold" />, label: t('durée', 'Durée'), value: data.duration },
              { icon: <CreditCard className="w-5 h-5 text-gold" />, label: t('crédits', 'Crédits'), value: data.credits },
              { icon: <GraduationCap className="w-5 h-5 text-gold" />, label: t('niveau', 'Niveau'), value: data.level },
              { icon: <Award className="w-5 h-5 text-gold" />, label: t('superviseur', 'Superviseur'), value: program.superviseur.split(' — ')[0] }
            ].map((item, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center gap-2">{item.icon}<span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{item.label}</span></div>
                <div className="text-sm font-black text-navy uppercase tracking-tight leading-tight">{item.value}</div>
              </div>
            ))}
          </section>

          {/* Description */}
          <section id="overview" className="space-y-8">
            <h2 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight italic border-b-2 border-gray-100 pb-4 flex items-center gap-4">
              <BookOpen className="w-7 h-7 text-gold" /> {t('presentation_filiere', 'Présentation de la Filière')}
            </h2>
            <p className="text-gray-600 font-medium leading-relaxed text-lg">{data.description}</p>

            {/* Supervisor with logo */}
            <div className="flex items-center gap-6 p-6 bg-gray-50 border-l-8 border-navy rounded-sm">
              <img src={program.superviseurLogo} alt={program.superviseur} className="h-14 w-auto object-contain" />
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-gold mb-1">{t('tutelle_academique', 'Tutelle Académique')}</div>
                <div className="text-navy font-black text-sm uppercase tracking-tighter">{program.superviseur}</div>
              </div>
            </div>
          </section>

          {/* Objectives */}
          <section id="objectives" className="space-y-8">
            <h2 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight italic border-b-2 border-gray-100 pb-4 flex items-center gap-4">
              <CheckCircle2 className="w-7 h-7 text-gold" /> {t('objectifs_pedagogiques', 'Objectifs Pédagogiques')}
            </h2>
            <ul className="space-y-4">
              {data.objectives.map((obj, idx) => (
                <li key={idx} className="flex items-start gap-4 group">
                  <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-gold/20 transition-colors">
                    <span className="text-gold font-black text-xs">{idx + 1}</span>
                  </div>
                  <span className="text-gray-600 font-medium leading-relaxed">{obj}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Modules / Curriculum */}
          <section id="curriculum" className="space-y-10">
            <h2 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight italic border-b-2 border-gray-100 pb-4 flex items-center gap-4">
              <GraduationCap className="w-7 h-7 text-gold" /> {t('programme_modules', 'Programme & Modules')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {data.modules.map((mod, idx) => (
                <div key={idx} className="bg-gray-50 border border-gray-100 p-6 rounded-sm hover:shadow-xl hover:border-gold transition-all duration-300 group">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-lg bg-navy text-gold flex items-center justify-center font-black text-sm flex-shrink-0">
                      {idx + 1}
                    </div>
                    <h3 className="text-sm font-black text-navy uppercase tracking-tight leading-tight group-hover:text-gold transition-colors">{mod.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {mod.topics.map((topic, tidx) => (
                      <li key={tidx} className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* LMD Pathway */}
          <section id="pathway" className="bg-navy p-8 md:p-14 rounded-sm shadow-2xl relative overflow-hidden group">
            <div className="absolute bottom-0 right-0 w-80 h-80 bg-gold/5 rounded-full translate-y-1/2 translate-x-1/2 pointer-events-none group-hover:scale-110 transition-transform duration-1000" />
            <div className="relative z-10 space-y-10">
              <h2 className="text-2xl font-black text-white uppercase tracking-tighter italic text-center">{t('parcours_academique_lmd', 'Parcours Académique LMD')}</h2>
              <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:gap-0">
                {[
                  { label: t('programs_pathway_step_1'), sub: t('programs_pathway_duration_1') },
                  { label: t('programs_pathway_step_2'), sub: t('programs_pathway_duration_2') },
                  { label: t('programs_pathway_step_3'), sub: t('programs_pathway_duration_3') },
                  { label: t('programs_pathway_step_4'), sub: t('programs_pathway_duration_4') },
                  { label: t('programs_pathway_step_5'), sub: t('programs_pathway_duration_5') }
                ].map((step, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 rounded-full bg-gold/20 border-2 border-gold/40 flex items-center justify-center text-gold font-black text-sm">
                        {idx + 1}
                      </div>
                      <div className="text-[10px] font-black text-white uppercase tracking-widest mt-2">{step.label}</div>
                      <div className="text-[8px] text-white/40 font-medium">{step.sub}</div>
                    </div>
                    {idx < 4 && <ChevronRight className="w-4 h-4 text-gold/40 flex-shrink-0 hidden md:block" />}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Careers */}
          <section id="careers" className="space-y-8">
            <h2 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight italic border-b-2 border-gray-100 pb-4 flex items-center gap-4">
              <Briefcase className="w-7 h-7 text-gold" /> {t('debouches_professionnels', 'Débouchés Professionnels')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.careers.map((career, idx) => (
                <div key={idx} className="group bg-gray-50 border border-gray-100 p-5 rounded-sm hover:bg-white hover:shadow-xl hover:border-gold transition-all duration-300 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-navy/5 flex items-center justify-center flex-shrink-0 group-hover:bg-gold/10 transition-colors">
                    <Users className="w-5 h-5 text-navy/50 group-hover:text-gold transition-colors" />
                  </div>
                  <span className="text-sm font-black text-navy uppercase tracking-tight leading-tight group-hover:text-gold transition-colors">{career}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Certifications */}
          {data.certifications && data.certifications.length > 0 && (
            <section id="certifications" className="space-y-8">
              <h2 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tight italic border-b-2 border-gray-100 pb-4 flex items-center gap-4">
                <Award className="w-7 h-7 text-gold" /> {t('certifications_associees', 'Certifications Associées')}
              </h2>
              <div className="space-y-3">
                {data.certifications.map((cert, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-5 bg-gold/5 border-l-8 border-gold rounded-sm">
                    <Award className="w-5 h-5 text-gold flex-shrink-0" />
                    <span className="text-sm font-black text-navy uppercase tracking-tight">{cert}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="bg-gray-50 border-2 border-gray-100 p-8 md:p-14 rounded-sm text-center space-y-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2 h-full bg-gold" />
            <h2 className="text-2xl md:text-3xl font-black text-navy uppercase tracking-tighter italic leading-none">
              {t('candidatez_filiere', 'Candidatez à la Filière')} {data.title}
            </h2>
            <p className="text-gray-500 font-medium max-w-2xl mx-auto">
              {t('admissions_cta_desc', "Rejoignez la prochaine promotion et faites partie de la génération qui construit l'Afrique de demain. Candidatures ouvertes — Rentrée Octobre 2026.")}
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/admissions" className="bg-navy text-white px-12 py-5 font-black uppercase tracking-widest text-xs shadow-xl hover:bg-gold hover:text-navy transition-all active:scale-95 flex items-center gap-3">
                {t('common_apply_now', 'Candidater Maintenant')} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="border-2 border-navy text-navy px-12 py-5 font-black uppercase tracking-widest text-xs hover:bg-navy hover:text-white transition-all active:scale-95">
                {t('common_meet_advisor', 'Parler à un Conseiller')}
              </Link>
            </div>
          </section>

          {/* Prev / Next Navigation */}
          <div className="flex justify-between gap-8 pt-4 border-t border-gray-100">
            {prevProgram ? (
              <Link to={`/programs/${prevProgram.slug}`} className="group flex items-center gap-4 hover:text-gold transition-colors">
                <ChevronRight className="w-5 h-5 rotate-180 text-gold" />
                <div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-1">{t('filiere_precedente', 'Filière Précédente')}</div>
                  <div className="text-sm font-black text-navy group-hover:text-gold transition-colors uppercase tracking-tighter">{(prevProgram.translations[currentLang] || prevProgram.translations.fr).title}</div>
                </div>
              </Link>
            ) : <div />}
            {nextProgram ? (
              <Link to={`/programs/${nextProgram.slug}`} className="group flex items-center gap-4 text-right hover:text-gold transition-colors">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-1">{t('filiere_suivante', 'Filière Suivante')}</div>
                  <div className="text-sm font-black text-navy group-hover:text-gold transition-colors uppercase tracking-tighter">{(nextProgram.translations[currentLang] || nextProgram.translations.fr).title}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-gold" />
              </Link>
            ) : <div />}
          </div>

        </div>
      </main>
    </div>
  );
}
